import classNames from 'classnames';
import {
  RefObject, SyntheticEvent, useEffect, useRef, useState,
} from 'react';
import PlayButton from '../Video/PlayButton';
import styles from './VideoControlsOverlay.module.scss';
import { useOutsideClick } from '../../useOutsideClick';

interface IVideoControlsOverlayProps {
  handleClose?: (e?: SyntheticEvent) => void;
  togglePlay: () => void;
  toggleSpeed: () => void;
  toggleMute: () => void;
  isMuted: boolean;
  isPlaying: boolean;
  isSpeededUp: boolean;
  videoRef: RefObject<HTMLVideoElement>;
}

const playIcon = 'media/player/play_icon.svg#resumeIconSVG';
const pauseIcon = 'media/player/pause_icon.svg#pauseIconSVG';

const muteIcon = 'media/player/sound_icon.svg#soundIconSVG';
const mutedIcon = 'media/player/muted_icon.svg#mutedIconSVG';

const defaultSpeedIcon = 'media/player/default_speed_icon.svg#defaultSpeedIconSVG';
const doubledSpeedIcon = 'media/player/doubled_speed_icon.svg#doubledSpeedIconSVG';

const VideoControlsOverlay = ({
  handleClose = () => null,
  togglePlay,
  toggleSpeed,
  isMuted,
  isPlaying,
  isSpeededUp,
  toggleMute,
  videoRef,
}: IVideoControlsOverlayProps) => {
  const [showControls, setShowControls] = useState<boolean>(false);
  const [showControlButtons, setShowControlButtons] = useState<boolean>(false);
  const hoveredOverlayRef = useRef<boolean>(false);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const muteRef = useRef<HTMLButtonElement>(null);
  const progressRef = useRef<HTMLProgressElement>(null);
  useOutsideClick(videoWrapperRef, handleClose);

  const handleMouseMove = () => {
    if (hoveredOverlayRef.current) {
      setShowControls(true);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      const timeout = setTimeout(() => setShowControls(false), 2000);
      timeoutRef.current = timeout;
    }
  };

  const hideControls = () => {
    handleMouseMove();
  };

  const handleOverlayHover = () => {
    setShowControls(true);
    hoveredOverlayRef.current = true;
    const timeout = setTimeout(() => setShowControls(false), 2000);
    timeoutRef.current = timeout;
    window.addEventListener('mousemove', hideControls);
  };

  const handleOverlayUnHover = () => {
    setShowControls(false);
    hoveredOverlayRef.current = false;
    window.addEventListener('mousemove', hideControls);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const getProgress = () => {
    if (videoRef?.current) {
      const videoElement = videoRef?.current as HTMLVideoElement;
      const videoDuration = videoElement.duration;

      progressRef.current?.setAttribute('max', videoDuration?.toString() || '1');
    }
  };

  const handleTimeUpdate = () => {
    const currentMax = progressRef.current?.getAttribute('max');

    if (!currentMax) {
      getProgress();
    }
    const time = videoRef?.current?.currentTime;
    progressRef.current?.setAttribute('value', time?.toString() || '0');
  };

  const handleProgressClick = (event: PointerEvent) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const maxValue = Number(progressRef.current.getAttribute('max')) || 0;

      const clickX = event.clientX - rect.left;

      const { width } = rect;
      const percent = Math.round((clickX / width) * 100);
      const newValue = (+maxValue * (percent / 100));
      const progressValue = Math.max(0, Math.min(maxValue, newValue));

      const video = videoRef.current;

      if (video) video.currentTime = progressValue || 0;
    }
  };

  useEffect(() => {
    if (progressRef.current && videoRef?.current) {
      videoRef?.current.addEventListener('loadedmetadata', getProgress);
      videoRef?.current.addEventListener('timeupdate', handleTimeUpdate);
      progressRef?.current.addEventListener('click', (event) => handleProgressClick(event as PointerEvent));
    }
  }, [progressRef.current, videoRef?.current]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('mousemove', hideControls);
    }
    videoRef?.current?.addEventListener('loadedmetadata', getProgress);
    videoRef?.current?.addEventListener('timeupdate', handleTimeUpdate);
    progressRef.current?.removeEventListener('click', (event) => handleProgressClick(event as PointerEvent));
  }, []);

  const soundIcon = isMuted ? mutedIcon : muteIcon;
  const stateIcon = isPlaying ? pauseIcon : playIcon;
  const speedIcon = isSpeededUp ? doubledSpeedIcon : defaultSpeedIcon;

  return (
    <div
      className={classNames(styles.controlsOverlay, {
        [styles.displayedControls]: showControls,
      })}
      onMouseEnter={handleOverlayHover}
      onMouseLeave={handleOverlayUnHover}
    >
      <button
        type='button'
        className={classNames(styles.controlButton, styles.closeButton)}
        onClick={(e) => handleClose?.(e)}
      >
        <svg>
          <use
            href='media/close.svg#closeSVG'
            xlinkHref='media/close.svg#closeSVG'
          />
        </svg>
      </button>
      <div
        id='video-controls'
        className={styles.controls}
        data-state='hidden'
      >
        <button
          type='button'
          className={styles.playPauseButton}
          onClick={togglePlay}
        >
          <svg>
            <use
              href={stateIcon}
              xlinkHref={stateIcon}
            />
          </svg>
        </button>
        <div className={styles.progressWrapper}>
          <progress
            id='progress'
            value='0'
            className={styles.videoProgress}
            ref={progressRef}
          >
            <span
              id='progress-bar'
              className={styles.progressBar}
            />
          </progress>
        </div>
        <div className={styles.sideControls}>
          <div className={classNames(styles.controlButtons, {
            [styles.shown]: showControlButtons,
          })}
          >
            <button
              type='button'
              className={styles.controlButton}
              onClick={toggleMute}
              ref={muteRef}
            >
              <svg>
                <use
                  href={soundIcon}
                  xlinkHref={soundIcon}
                />
              </svg>
            </button>
            <button
              type='button'
              className={styles.controlButton}
              onClick={toggleSpeed}
            >
              <svg>
                <use
                  href={speedIcon}
                  xlinkHref={speedIcon}
                />
              </svg>
            </button>
          </div>
          <button
            type='button'
            className={classNames(styles.controlButton, styles.controlsToggleBtn, {
              [styles.active]: showControlButtons,
            })}
            onClick={() => setShowControlButtons(!showControlButtons)}
          >
            <svg>
              <use
                href='media/player/next_icon.svg#nextIconSVG'
                xlinkHref='media/player/next_icon.svg#nextIconSVG'
              />
            </svg>
          </button>
        </div>
      </div>
      <PlayButton
        isPlaying={isPlaying}
        onClick={togglePlay}
        playButtonClassName={styles.videoButton}
      />
    </div>
  );
};

export default VideoControlsOverlay;
